<template lang="html">
    <div class="sign-up-register">
        <vs-row class="sign-up-register__content">
            <vs-col vs-xs="12" vs-sm="6" vs-lg="6">
                <vs-input
                    label="Nome"
                    v-model="form.name"
                    class="sign-up-register__content-input required-field"
                    :danger="invalidField.name"
                    :danger-text="alertMessage.name"
                    placeholder="Digite seu nome completo"
                    @blur="checkValidName"
                    @input="handleChange('name')"
                    @keyup="form.name = onlyFirstLetterCapitalized($event.target.value)"
                />
            </vs-col>

            <vs-col vs-xs="6" :vs-sm="6" vs-lg="6">
                <vs-input
                    label="Celular"
                    v-model="form.phone"
                    class="sign-up-register__content-input required-field"
                    v-mask="['(##) ####-####', '(##) #####-####']"
                    placeholder="(00)0000-0000"
                    :danger="invalidField.phone"
                    :danger-text="alertMessage.phone"
                    @blur="checkValidPhone"
                    @input="handleChange('phone')"
                />
            </vs-col>

            <vs-col vs-xs="12" vs-sm="6" vs-lg="6">
                <vs-input
                    label="E-mail"
                    v-model="form.email"
                    class="sign-up-register__content-input required-field"
                    :danger="invalidField.email"
                    :danger-text="alertMessage.email"
                    placeholder="contato@contato.com.br"
                    @blur="checkValidEmail"
                    @input="handleChange('email')"
                />
            </vs-col>

            <vs-col vs-xs="6" vs-sm="6" vs-lg="6">
                <vs-input
                    label="CPF"
                    v-model="form.cpf"
                    class="sign-up-register__content-input required-field"
                    v-mask="['###.###.###-##']"
                    placeholder="000.000.000-00"
                    :danger="invalidField.cpf"
                    :danger-text="alertMessage.cpf"
                    @blur="checkValidCpf"
                    @input="handleChange('cpf')"
                />
            </vs-col>
        </vs-row>
    </div>
</template>

<script>
import {computed, defineComponent} from "vue"
import mixins from "@/mixins/mixins.js"
import {useStore} from "@/store/store.js"
import {trackAddToCart} from "@/helpers/gtmEvents";

export default defineComponent({
    name: 'SignUpRegister',

    mixins: [mixins],

    props: {
        values: {
            type: Object,
            default: () => ({})
        },
        simple: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        error: {
            type: Boolean,
            default: false
        },
        plan: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            form: {
                cpf: '',
                email: '',
                name: '',
                phone: ''
            },
            invalidField: {
                cpf: false,
                email: false,
                name: false,
                phone: false
            },
            alertMessage: {
                cpf: '',
                email: '',
                name: '',
                phone: ''
            },
            firstTypingTriggered: false
        }
    },

    computed: {
        isAllChecked() {
            Object.keys(this.alertMessage).forEach((field) => {
                this.invalidField[field] = !!this.alertMessage[field]
            })

            return Object.values(this.invalidField).filter(val => val).length === 0
        },
        computedGtmData() {
            return {
                ecommerce: {
                    currency: "BRL",
                    value: this.planValue,
                    items: [{
                        item_id: this.plan.id,
                        item_name: this.plan.name,
                        discount: this.plan.discount,
                        item_brand: "Cartão Dasa",
                        price: this.plan.planValue,
                        quantity: 1
                    }]
                }
            }
        }
    },

    watch: {
        error(val) {
            if (val) {
                this.checkValidFields()
            }
        },
        values() {
            this.setFormValues()
        }
    },

    setup() {
        const store = useStore()

        return {
            isMobile: computed(() => store.state.isMobile),
            validateRegisteredCpf: (cpf) => store.dispatch('validateCpf', cpf),
            validateRegisteredEmail: (email) => store.dispatch('validateEmail', email),
        }
    },

    mounted() {
        this.setFormValues()
        Object.keys(this.form).forEach((field) => this.clearErrors(field))

        if (this.error) {
            this.checkValidFields()
        }
    },

    methods: {
        clearErrors(field) {
            this.invalidField[field] = false
            this.alertMessage[field] = null
        },

        setFormValues() {
            Object.keys(this.form).forEach((field) => {
                if (this.values && Object.hasOwn(this.values, field)) {
                    this.form[field] = this.values[field]
                }
            })
        },

        handleChange(field) {
            this.clearErrors(field);

            if (field === 'name' && !this.firstTypingTriggered) {
                trackAddToCart(this.computedGtmData);
                this.firstTypingTriggered = true;
            }

            this.$emit('set:user', this.form);
            this.$emit('set:user-errors', this.invalidField);
        },

        async checkValidName() {
            this.clearErrors('name')

            if (!this.form.name) {
                this.alertMessage.name = "Campo obrigatório"
            } else if (!this.testFullName(this.form.name)) {
                this.alertMessage.name = "Insira o nome completo"
            }

            this.invalidField.name = !!this.alertMessage.name
            this.$emit('set:user', this.form)
        },

        async checkValidPhone() {
            this.clearErrors('phone')

            const phone = this.form.phone.replace(/[^\d]+/g, "")

            if (!phone) {
                this.alertMessage.phone = "Campo obrigatório"
            } else if (phone.length < 10) {
                this.alertMessage.phone = "Número inválido"
            }

            this.invalidField.phone = !!this.alertMessage.phone
            this.$emit('set:user', this.form)
        },

        async checkValidEmail() {
            this.clearErrors('email')

            if (!this.form.email) {
                this.alertMessage.email = "Campo obrigatório"
            } else if (!this.validateEmail(this.form.email)) {
                this.alertMessage.email = "Insira um E-mail válido"
            } else {
                const isUsedEmail = await this.validateRegisteredEmail(this.form.email)

                if (isUsedEmail) {
                    this.alertMessage.email = "E-mail já cadastrado"
                    this.showNotifyWarning(
                        `Usuário já cadastrado com este E-MAIL. Para usar os serviços Dasa+Saúde realize o login.`,
                        3200
                    )
                }
            }

            this.invalidField.email = !!this.alertMessage.email
            this.$emit('set:user', this.form)
            this.$emit('set:user-errors', this.invalidField)
        },

        async checkValidCpf() {
            this.clearErrors('cpf')

            if (!this.form.cpf) {
                this.alertMessage.cpf = "Campo obrigatório"
            } else {
                const cpf = this.form.cpf.replace(/[^\d]+/g, "")

                if (cpf.length < 11) {
                    this.alertMessage.cpf = "Insira um CPF válido"
                } else if (!this.validateCPF(cpf)) {
                    this.alertMessage.cpf = "CPF Inválido"
                } else {
                    const isUsedCpf = await this.validateRegisteredCpf(cpf)

                    if (isUsedCpf) {
                        this.alertMessage.cpf = "CPF já cadastrado"
                        this.showNotifyWarning(
                            `Usuário já cadastrado com este CPF. Para usar os serviços Dasa+Saúde realize o login.`,
                            3200
                        )
                    }
                }
            }

            this.invalidField.cpf = !!this.alertMessage.cpf
            this.$emit('set:user', this.form)
        },

        async checkValidFields() {
            await this.checkValidName()
            await this.checkValidPhone()
            await this.checkValidEmail()
            await this.checkValidCpf()

            return this.isAllChecked
        }
    }
})
</script>

<style lang="scss" scoped>
.sign-up-register {
    &__header {
        &-title {
            font-family: Roboto;
            font-size: 1rem;
            font-weight: 700;
            line-height: 25.78px;
            text-align: left;

            @media #{$xl-and-up} {
                font-size: 1.5rem;
            }
        }
    }

    &__content {
        row-gap: .5rem;
    }

    &::v-deep {
        .vs-con-input-label {
            width: 100%;
            color: #282828;
            text-transform: none;
        }

        .vs-input {
            text-transform: none;

            &-primary.isFocus {
                .vs-input--label {
                    color: #282828;
                }
            }

            &--input:focus {
                border-color: #0137FD !important;
            }

            vs-input--input::placeholder {
                color: #4F4F4F !important;
                align-items: center;
            }
        }

    }
}
</style>
